import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { CatenaLinkedFleet } from '~hooks/useCatena/models';
import useCatenaConnect from '~hooks/useCatena/useCatenaConnect';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import CatenaEmptyState from '~pages/Settings/Integrations/CatenaEmptyState';
import {
  actionsColDef,
  CatenaLinkedFleetRow,
  invitationColDef,
  statusColDef,
} from '~pages/Settings/Integrations/catenaLinkedFleetsDataGridColumnsDefinition';
import { ROUTES as routes } from '~router/routePaths';
import { PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getPageNumber } from '~utils/pagination';

interface LinkedFleetsPage {
  cursor?: string;
  direction?: PaginationLink;
  page: number;
}

const FIRST_PAGE: LinkedFleetsPage = { page: 1 };

const CatenaManagement = observer(() => {
  return <CatenaManagementAccess component={CatenaManagementPage} />;
});

const CatenaManagementPage = observer(() => {
  const { toasterStore } = useStores();
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const { canConnect, connect, disconnect, isLoading, linkedFleets, pagination } =
    useCatenaConnect(currentPage);

  const [isConnecting, setIsConnecting] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<CatenaLinkedFleet | null>(
    null,
  );
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    try {
      if (await connect()) setCurrentPage(FIRST_PAGE);
    } finally {
      setIsConnecting(false);
    }
  }, [connect]);

  const handleDisconnectConfirm = useCallback(async () => {
    if (!disconnectTarget) return;

    setIsDisconnecting(true);
    try {
      if (!(await disconnect(disconnectTarget.id))) return;

      setDisconnectTarget(null);
      setCurrentPage(FIRST_PAGE);
      toasterStore.push(
        alert(
          t('administration.integration.catena.toast.disconnected'),
          AlertTypes.success,
        ),
      );
    } finally {
      setIsDisconnecting(false);
    }
  }, [disconnectTarget, disconnect]);

  const goToPage = useCallback(
    (direction?: PaginationLink) => {
      const cursor = direction ? pagination?.[direction] : undefined;

      setCurrentPage((page) =>
        direction && cursor
          ? { cursor, direction, page: getPageNumber(page.page, direction) }
          : FIRST_PAGE,
      );
    },
    [pagination],
  );

  const columns = useMemo(() => [statusColDef, invitationColDef, actionsColDef], []);

  const rows = useMemo<CatenaLinkedFleetRow[]>(
    () =>
      linkedFleets.map((fleet) => ({
        id: fleet.id,
        fleet,
        onDisconnect: () => setDisconnectTarget(fleet),
      })),
    [linkedFleets],
  );

  // First-time onboarding banner gates on emptiness only; `!isLoading` avoids a flash
  // before the initial listLinkedFleets() fetch resolves.
  const shouldShowEmptyState = !isLoading && rows.length === 0;

  return (
    <Box sx={{ maxWidth: '1000px' }}>
      <Button
        color="secondary"
        component={Link}
        startIcon={<ArrowBackIosNew fontSize="small" />}
        to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
        variant="text"
      >
        {t('administration.integration.title')}
      </Button>
      <SettingsPageHeader
        title={t('administration.integration.catena.name')}
        description={t('administration.integration.catena.description')}
      />

      {shouldShowEmptyState ? (
        <CatenaEmptyState
          disabled={!canConnect}
          isConnecting={isConnecting}
          onConnect={handleConnect}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={!canConnect}
            loading={isConnecting}
            onClick={handleConnect}
          >
            {t('administration.integration.catena.cta.connect')}
          </SmallLoadingButton>
        </Box>
      )}

      <DataTable
        id="catena-linked-fleets-grid"
        columns={columns}
        loading={isLoading}
        rows={rows}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isLoading}
            onNavigate={goToPage}
            pagination={pagination && { ...pagination, page: currentPage.page }}
          />
        }
      />

      <ConfirmationDialog
        confirmButtonProps={{
          children: t('administration.integration.catena.cta.disconnect'),
        }}
        isBusy={isDisconnecting}
        isOpen={disconnectTarget !== null}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={handleDisconnectConfirm}
        title={t('administration.integration.catena.disconnect_confirmation_title')}
      >
        <Typography>
          {t('administration.integration.catena.disconnect_confirmation_body')}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
});

interface CatenaManagementAccessProps {
  component: React.ComponentType;
}

const CatenaManagementAccess = observer(
  ({ component: Component }: CatenaManagementAccessProps) => {
    const { userStore } = useStores();
    const navigate = useNavigate();

    const hasAccess = userStore.getPermissions().canManageTelematicsIntegration;

    if (!hasAccess) {
      navigate(routes.base);
      return null;
    }

    return <Component />;
  },
);

export default CatenaManagement;
